// The call (/call/:id). One WebSocket speaking Otto Live Protocol v1, the mic
// worklet at 16 kHz in 20 ms frames, the player worklet at 24 kHz, the 3D Otto
// or the 2D Otto card, the agenda, captions, and the mode toggle
// (design 3.2 rows 2 to 4, 4.3, 4.4, 7; Wes's Sept 28 decisions).
//
// Arriving from the check-in tap, the call starts on its own (audio was
// unlocked in that tap). Arriving any other way (a reload, a return), it waits
// for a Rejoin tap, because browsers need a gesture to play sound.
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { DuckSettings, MicSettings } from "@otto/config";
import type { CheckinResponse, ClientAudioSettings, LiveTokenResponse } from "@otto/api";
import type { z } from "zod";
import { getConsultStatus, getLiveToken, sendFunnel } from "./api";
import type { AvatarStats } from "./avatar/OttoAvatar";
import type { Mood } from "./avatar/avatarMotion";
import { MOOD_CUE_HOLD_MS, moodCue } from "./avatar/moodCues";
import { primeCallAudio } from "./call/audio/audioPrime";
import { MicCapture } from "./call/audio/micCapture";
import { gateOptionsFrom, gateOptionsWithQuery } from "./call/audio/micGate";
import { OttoPlayer } from "./call/audio/player";
import { CallController, type CallDeps, type CallSetup, type CallView } from "./call/callController";
import { CallConnecting, CallFailed, CallMissing, CallRejoin, MicHelp } from "./call/CallGates";
import { CallStage } from "./call/CallStage";
import { LiveClient, liveSocketUrl } from "./call/liveClient";
import { createWakeLock } from "./call/wakeLock";
import { deviceClass, inAppBrowser, readDeviceHints, usesPhoneModel } from "./device";
import { liveTokenExpired, readConsultRecord, saveConsultRecord, updateConsultRecord, type ConsultTabRecord } from "./session";
import { SITE, stringsFor } from "./strings";
import "./consult.css";

const DevCallReview = import.meta.env.DEV ? lazy(() => import("./call/DevCallReview")) : null;

/** Statuses after which the call page has nothing to do: the thank-you page takes over. */
const AFTER_CALL = new Set(["ended", "packaging", "packaged", "held", "failed", "withdrawn"]);

interface NavState {
  fromCheckin?: boolean;
  checkin?: z.infer<typeof CheckinResponse>;
  firstName?: string;
}

function recordFromNav(state: NavState | null, consultId: string): ConsultTabRecord | null {
  const checkin = state?.checkin;
  if (!checkin || checkin.consult_id !== consultId) return null;
  return {
    consult_id: checkin.consult_id,
    live_path: checkin.live_path,
    live_origin: checkin.live_origin,
    live_token: checkin.live_token,
    live_token_expires_at: checkin.live_token_expires_at,
    mode: checkin.mode,
    language: checkin.language,
    first_name: state?.firstName ?? "",
    business_name: "",
    agenda: checkin.agenda,
    target_minutes: checkin.target_minutes,
    cap_minutes: checkin.cap_minutes,
    client_audio: checkin.client_audio,
    started: false,
    last_seen_at: null,
    last_turn_played: null,
  };
}

/**
 * A tab that never checked in (a new tab, or storage blocked): the HttpOnly
 * consult cookie gets a fresh live token and the call's details from otto-app.
 * It rejoins as a resume: the call may already be under way.
 */
function recordFromLiveToken(token: z.infer<typeof LiveTokenResponse>): ConsultTabRecord {
  return {
    consult_id: token.consult_id,
    live_path: token.live_path,
    live_origin: token.live_origin,
    live_token: token.live_token,
    live_token_expires_at: token.live_token_expires_at,
    mode: token.mode,
    language: token.language,
    first_name: token.first_name,
    business_name: "",
    agenda: token.agenda,
    target_minutes: token.target_minutes,
    cap_minutes: token.cap_minutes,
    client_audio: token.client_audio,
    started: true,
    last_seen_at: null,
    last_turn_played: null,
  };
}

/** Lab overrides from the URL, kept from today's consult for tuning without a deploy (?duck=1&lipDelay=0). */
function queryNumber(search: string, key: string): number | undefined {
  const raw = new URLSearchParams(search).get(key);
  if (raw === null || raw.trim() === "") return undefined;
  const value = Number(raw);
  return Number.isFinite(value) && value >= 0 ? value : undefined;
}

function buildSetup(record: ConsultTabRecord, search: string): CallSetup {
  const audio: ClientAudioSettings | null = record.client_audio;
  const hints = readDeviceHints();
  const mic = MicSettings.parse(audio?.mic ?? {});
  const duck = DuckSettings.parse(audio?.duck ?? {});
  const duckQuery = new URLSearchParams(search).get("duck");
  const lipDelay = queryNumber(search, "lipDelay") ?? audio?.lipsync_delay_ms ?? undefined;
  return {
    consultId: record.consult_id,
    liveUrl: liveSocketUrl(record.live_path, window.location, record.live_origin),
    liveToken: record.live_token,
    mode: record.mode,
    agenda: record.agenda,
    targetMinutes: record.target_minutes,
    capMinutes: record.cap_minutes,
    resumeFromLastSeenAt: record.started ? (record.last_seen_at ?? Date.now()) : null,
    lastTurnPlayed: record.last_turn_played,
    client: { device_class: deviceClass(hints), in_app_browser: Boolean(inAppBrowser(hints.userAgent)) },
    gate: gateOptionsWithQuery(gateOptionsFrom(mic), search),
    micSettings: mic,
    duck,
    // The DUCK_ENABLED switch ships OFF; the server turns it on (client_audio, pinned at check-in), or ?duck=1 in the lab.
    duckEnabled: duckQuery === "1" ? true : duckQuery === "0" ? false : Boolean(audio?.duck_enabled),
    ...(lipDelay !== undefined ? { lipSyncDelayMs: lipDelay } : {}),
  };
}

const noSubscribe = () => () => undefined;
const IDLE_VIEW: CallView = {
  phase: "idle",
  micProblem: null,
  failure: null,
  endedReason: null,
  finished: false,
  serverRecovering: false,
  resumed: false,
  agenda: [],
  targetMinutes: 15,
  capMinutes: 22,
  agendaIndex: 0,
  clock: { seconds: 0, receivedAt: 0, paused: true },
  extension: { offered: false, accepted: null, answered: null },
  mode: "video",
  muted: false,
  micState: "idle",
  audioBlocked: false,
  ottoState: "idle",
  caption: { turn: null, text: "", startedAt: null },
  reconnectAttempt: 0,
  mood: "neutral",
};
const idleView = () => IDLE_VIEW;

function realDeps(consultId: string): CallDeps {
  return {
    createPlayer: (options) => new OttoPlayer(options),
    createMic: (options) => new MicCapture(options),
    createClient: (options) => new LiveClient(options),
    wakeLock: createWakeLock(),
    now: () => Date.now(),
    perfNow: () => performance.now(),
    schedule: (fn, ms) => setTimeout(fn, ms),
    cancel: (handle) => clearTimeout(handle as ReturnType<typeof setTimeout>),
    remember: (patch) => updateConsultRecord(consultId, patch),
  };
}

export function CallPage() {
  const { id = "" } = useParams();
  const location = useLocation();
  const navState = (location.state ?? null) as NavState | null;
  if (import.meta.env.DEV && id === "dev-review" && DevCallReview) {
    return (
      <Suspense fallback={null}>
        <DevCallReview />
      </Suspense>
    );
  }
  const stored = readConsultRecord(id) ?? recordFromNav(navState, id);
  const usable = stored && !liveTokenExpired(stored) ? stored : null;
  return <CallPageFor consultId={id} record={usable} fromCheckin={Boolean(navState?.fromCheckin)} />;
}

function CallPageFor({ consultId, record, fromCheckin }: { consultId: string; record: ConsultTabRecord | null; fromCheckin: boolean }) {
  // No usable record in this tab: ask otto-app for a fresh token with the consult cookie.
  const [fetched, setFetched] = useState<{ state: "idle" | "loading" | "missing"; record: ConsultTabRecord | null }>({
    state: record ? "idle" : "loading",
    record: null,
  });
  useEffect(() => {
    if (record) return undefined;
    let cancelled = false;
    void getLiveToken(consultId).then((result) => {
      if (cancelled) return;
      if (!result.ok) {
        setFetched({ state: "missing", record: null });
        return;
      }
      const fresh = recordFromLiveToken(result.data);
      saveConsultRecord(fresh);
      setFetched({ state: "idle", record: fresh });
    });
    return () => {
      cancelled = true;
    };
  }, [consultId, record]);
  const active = record ?? fetched.record;
  const s = stringsFor(active?.language ?? "en");
  if (!active) {
    return (
      <div className="consult-root oo-consult-root oo-consult-root--call">
        <div className="oo-consult-content">
          {fetched.state === "loading" ? (
            <CallConnecting s={s} title={s.call.connectingTitle} detail={s.call.connectingDetail} />
          ) : (
            <CallMissing s={s} />
          )}
        </div>
      </div>
    );
  }
  return <LiveCall record={active} fromCheckin={fromCheckin} />;
}

function LiveCall({ record, fromCheckin }: { record: ConsultTabRecord; fromCheckin: boolean }) {
  const navigate = useNavigate();
  const s = stringsFor(record.language);
  const consultId = record.consult_id;
  const [attempt, setAttempt] = useState(0);
  // One controller per attempt ("Try again" after a failure starts clean). It is
  // created inside the effect that disposes it, so a remount (React StrictMode
  // runs mount, unmount, mount in development) never leaves a disposed
  // controller in use.
  const [controller, setController] = useState<CallController | null>(null);
  const recordRef = useRef(record);
  useEffect(() => {
    const created = new CallController(buildSetup(readConsultRecord(consultId) ?? recordRef.current, window.location.search), realDeps(consultId));
    setController(created);
    return () => {
      created.dispose();
      setController((current) => (current === created ? null : current));
    };
  }, [attempt, consultId]);
  const view = useSyncExternalStore(controller?.subscribe ?? noSubscribe, controller?.getSnapshot ?? idleView);
  const [gate, setGate] = useState<"auto" | "rejoin" | "checking">(fromCheckin ? "auto" : "checking");
  const [starting, setStarting] = useState(false);
  const statsRef = useRef<AvatarStats & { loadMs: number | null; fellBack: boolean }>({
    fpsAvg: null,
    contextLost: false,
    loadMs: null,
    fellBack: false,
  });
  const connectedOnce = useRef(false);
  // The stage's expression cue from the server drives the 3D Otto's lids and smile (design 7.2), and a cue phrase in
  // Otto's own latest words swaps in its mood for a few seconds (moodCues.ts, Sept 29 2026).
  const moodRef = useRef<Mood>("neutral");
  const [cueMood, setCueMood] = useState<Mood | null>(null);
  const captionTail = view.caption.text.slice(-120);
  useEffect(() => {
    const mood = moodCue(captionTail);
    if (!mood) return;
    setCueMood(mood);
    const timer = window.setTimeout(() => setCueMood(null), MOOD_CUE_HOLD_MS);
    return () => window.clearTimeout(timer);
  }, [captionTail]);
  moodRef.current = cueMood ?? view.mood;
  const hints = useMemo(() => readDeviceHints(), []);
  const variant = usesPhoneModel(hints) ? "phone" : "desktop";

  // Straight from the check-in tap: start now. Otherwise see whether the call already ended.
  useEffect(() => {
    if (gate === "auto") {
      void controller?.start();
      return;
    }
    if (gate !== "checking") return;
    let cancelled = false;
    void getConsultStatus(consultId).then((result) => {
      if (cancelled) return;
      if (result.ok && AFTER_CALL.has(result.data.status)) navigate(`/thanks/${consultId}`, { replace: true });
      else setGate("rejoin");
    });
    return () => {
      cancelled = true;
    };
  }, [consultId, controller, gate, navigate]);

  const rejoin = useCallback(() => {
    // Inside the tap: unlock audio before anything awaits (iOS).
    primeCallAudio();
    setStarting(true);
    setGate("auto");
  }, []);

  // The phone comes back, the network returns.
  useEffect(() => {
    if (!controller) return undefined;
    const onVisibility = () => {
      if (document.visibilityState === "visible") void controller.onVisible();
    };
    const onOnline = () => controller.onOnline();
    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("online", onOnline);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("online", onOnline);
    };
  }, [controller]);

  // Counts-only telemetry once a minute (design 7.4).
  useEffect(() => {
    if (!controller) return undefined;
    const timer = setInterval(() => {
      const stats = statsRef.current;
      controller.sendTelemetry({
        ...(stats.loadMs !== null ? { avatar_load_ms: stats.loadMs } : {}),
        ...(stats.fpsAvg !== null ? { fps_avg: Math.min(240, Math.round(stats.fpsAvg * 10) / 10) } : {}),
        webgl_context_lost: stats.contextLost,
        fell_back_to_2d: stats.fellBack,
      });
    }, 60_000);
    return () => clearInterval(timer);
  }, [controller]);

  useEffect(() => {
    const device = deviceClass(hints);
    const inApp = Boolean(inAppBrowser(hints.userAgent));
    if (view.phase === "live" && !connectedOnce.current) {
      connectedOnce.current = true;
      setStarting(false);
      sendFunnel({
        step: "call_connected",
        device_class: device,
        in_app_browser: inApp,
        mode: view.mode,
        language: record.language,
      });
    }
    if (view.finished) {
      sendFunnel({
        step: "call_ended",
        device_class: device,
        in_app_browser: inApp,
        mode: view.mode,
        language: record.language,
      });
      navigate(`/thanks/${consultId}`, { replace: true });
    }
  }, [consultId, hints, navigate, record.language, view.finished, view.mode, view.phase]);

  const callbackUrl = SITE.contact;
  let overlay: ReactNode = null;
  if (gate === "checking")
    overlay = <CallConnecting s={s} title={s.call.connectingTitle} detail={s.call.connectingDetail} />;
  else if (gate === "rejoin") overlay = <CallRejoin s={s} onStart={rejoin} starting={starting} />;
  else if (view.phase === "mic_failed" && view.micProblem)
    overlay = (
      <MicHelp
        s={s}
        problem={view.micProblem}
        userAgent={hints.userAgent}
        onRetry={() => void controller?.rejoin()}
        callbackUrl={callbackUrl}
      />
    );
  else if (view.phase === "failed")
    overlay = (
      <CallFailed
        s={s}
        code={view.failure?.code ?? "internal"}
        canRetry={
          ![
            "already_connected",
            "token_expired",
            "bad_token",
            "wrong_consult",
            "consult_not_found",
            "consult_ended",
          ].includes(view.failure?.code ?? "")
        }
        retrying={false}
        onRetry={() => {
          primeCallAudio();
          setAttempt((n) => n + 1);
          setGate("auto");
        }}
      />
    );
  else if (
    !connectedOnce.current &&
    (view.phase === "idle" || view.phase === "starting" || view.phase === "connecting")
  )
    overlay = <CallConnecting s={s} title={s.call.connectingTitle} detail={s.call.connectingDetail} />;

  const showStage = controller !== null && gate === "auto" && view.phase !== "mic_failed" && view.phase !== "failed";

  return (
    <div className="consult-root oo-consult-root oo-consult-root--call" lang={record.language}>
      <div className="oo-consult-content">
        <div className="consult-call-room">
          {showStage && controller && (
            // Mounted while connecting (hidden) so the 3D Otto loads during the connect, never after it.
            <div
              className="consult-call-room__stage"
              aria-hidden={Boolean(overlay)}
              data-hidden={overlay ? "true" : undefined}
            >
              <CallStage
                s={s}
                view={view}
                controller={controller}
                firstName={record.first_name}
                variant={variant}
                statsRef={statsRef}
                onFallback={() => undefined}
                moodRef={moodRef}
              />
            </div>
          )}
          {overlay}
        </div>
      </div>
    </div>
  );
}
